"use client";

import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import EyeOpen from "@/components/icons/eye-open";
import EyeClose from "@/components/icons/eye-close";
import Information from "@/components/icons/information-circle";

const Input = ({
  label,
  type = "text",
  passVal = false,
  placeholder,
  className = "",
  row = false,
  value,
  icon,
  textArea = false,
  defaultValue,
  onChange,
  star = false,
  error = "",
  toolTips,
  maxLength,
  minLength,
  pattern,
  onlyChar = false,
  allowMultipleDots = false,
  doorNo = false,
  address = false,
  alphanumeric = false,
  contractLoad = false,
  suggession = false,
  ...props
}) => {
  const [inputValue, setInputValue] = useState(value || defaultValue || "");
  const [typePassValue, setTypePassValue] = useState(false);

  useEffect(() => {
    if (value !== undefined) {
      setInputValue(value);
    }
  }, [value]);

  const handleInputChange = (e) => {
    let newValue = e.target.value;
    if (onlyChar) {
      newValue = newValue.replace(/[^A-Za-z ]/g, ""); // Only characters and spaces
    }
    if (type !== "email" || type !== 'mail') {
      newValue = newValue.toUpperCase();
    }
    setInputValue(newValue);
    if (onChange) onChange(newValue);
  };

  const handleKeyPress = (e) => {
    if (onlyChar) {
      if (!/[A-Za-z ]/.test(e.key)) {
        e.preventDefault();
      }

    } else if (allowMultipleDots) {
      if (!/[0-9.]/.test(e.key)) {
        e.preventDefault();
      }

    } else if (doorNo) {
      // ✅ Door No: letters, numbers, - and /
      if (!/[a-zA-Z0-9/-]/.test(e.key)) {
        e.preventDefault();
      }

    } else if (address) {
      if (!/[a-zA-Z0-9\-\.,\s]/.test(e.key)) {
        e.preventDefault();
      }

    } else if (alphanumeric) {
      if (!/[a-zA-Z0-9]/.test(e.key)) {
        e.preventDefault();
      }

    } else if (contractLoad) {
      if (!/[0-9.]/.test(e.key)) {
        e.preventDefault();
      }

    } else {
      if (!/[0-9]/.test(e.key)) {
        e.preventDefault();
      }
    }
  };


  const handlePaste = (e) => {
    const paste = e.clipboardData.getData("text");

    if (onlyChar) {
      if (!/^[A-Za-z ]+$/.test(paste)) {
        e.preventDefault();
      }

    } else if (allowMultipleDots) {
      if (!/^[0-9.]+$/.test(paste)) {
        e.preventDefault();
      }

    } else if (doorNo) {
      // ✅ Door No paste validation
      if (!/^[a-zA-Z0-9/-]+$/.test(paste)) {
        e.preventDefault();
      }

    } else if (address) {
      if (!/^[a-zA-Z0-9\-\.,\s]+$/.test(paste)) {
        e.preventDefault();
      }

    } else if (alphanumeric) {
      if (!/^[a-zA-Z0-9]+$/.test(paste)) {
        e.preventDefault();
      }

    } else if (contractLoad) {
      if (!/^\d+(\.\d{1,2})?$/.test(paste)) {
        e.preventDefault();
      }

    } else {
      if (!/^\d+$/.test(paste)) {
        e.preventDefault();
      }
    }
  };


  // ✅ Password input
  if (passVal) {
    return (
      <div className="relative mb-3">
        {label && (
          <label
            className="block text-sm font-medium text-gray-600 md:text-base"
            htmlFor={props.name}
          >
            {label}
            {star && (
              <span className="text-sm font-medium text-red-500">*</span>
            )}
          </label>
        )}
        <input
          type={typePassValue ? "text" : "password"}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          className={cn(
            `${props.disabled
              ? "bg-gray-200 opacity-60 cursor-not-allowed"
              : "bg-white"
            } text-black text-sm md:text-base w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-[1.5px] focus:ring-green-12 transition-all duration-300`,
            className
          )}
          {...props}
        />
        <div
          className={`absolute right-2 ${label ? "top-7 md:top-9" : "top-2.5"
            } h-full w-6 text-center cursor-pointer`}
          onClick={() => setTypePassValue(!typePassValue)}
        >
          {typePassValue ? <EyeOpen /> : <EyeClose />}
        </div>
        {error && <p className="text-sm font-medium text-red-500">{error}</p>}
      </div>
    );
  }

  // ✅ Textarea
  if (type === "textarea") {
    return (
      <div
        className={`input-container text-sm md:text-base mb-3 ${row && "flex flex-row gap-2"
          }`}
      >
        {label && (
          <label
            className={`block font-medium text-gray-600 ${row && "mt-2 text-nowrap"
              }`}
            htmlFor={props.name}
          >
            {label}
            {star && (
              <span className="text-red-500 text-sm font-medium">*</span>
            )}
          </label>
        )}
        <textarea
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          rows={2}
          className={cn(
            `${props.disabled
              ? "bg-gray-200 opacity-60 cursor-not-allowed"
              : "bg-white"
            } text-black text-sm md:text-base w-full px-3 py-2 border uppercase rounded-md focus:outline-none focus:ring-[1.5px] focus:ring-green-12 transition-all duration-300`,
            className
          )}
          {...props}
        />
        {error && <p className="text-sm font-medium text-red-500">{error}</p>}
      </div>
    );
  }

  // ✅ File input
  if (type === "file") {
    return (
      <div
        className={`input-container text-sm md:text-base mb-3 ${row && "flex flex-row gap-2"
          }`}
      >
        {label && (
          <label
            className={`block font-medium text-gray-600 ${row && "mt-2 text-nowrap"
              }`}
            htmlFor={props.name}
          >
            {label}

            {star && (
              <span className="text-sm font-medium text-red-500">*</span>
            )}
          </label>
        )}
        <div className="flex items-center">
          <input
            type="file"
            placeholder={placeholder}
            defaultValue={value}
            className={cn(
              `${props.disabled
                ? "bg-gray-200 opacity-60 cursor-not-allowed"
                : "bg-white"
              } text-black text-sm md:text-base w-full px-3 py-2 border border-gray-500 rounded-md focus:outline-none focus:ring-[1.5px] focus:ring-green-12 transition-all duration-300`,
              className
            )}
            {...props}
          />
          {toolTips && <Information label={toolTips} />}
        </div>
        {error && <p className="text-sm font-medium text-red-500">{error}</p>}
      </div>
    );
  }

  // ✅ Regular Input (text, email, number, etc.)
  return (
    <div
      className={`input-container text-sm md:text-base mb-3 ${row && "flex flex-row gap-2"
        }`}
    >
      {label && (
        <label
          className={`block font-medium text-gray-600 ${row && "mt-2 text-nowrap"
            }`}
          htmlFor={props.name}
        >
          {label}
          {star && <span className="text-sm font-medium text-red-500">*</span>}
          {suggession && <span className="text-sm font-medium text-red-500">({suggession})</span>}
        </label>
      )}
      <div className="relative flex items-center w-full">
        {icon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none">
            {icon}
          </div>
        )}
        <input
          type={type}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={pattern && pattern ? handleKeyPress : undefined}
          onPaste={pattern && pattern ? handlePaste : undefined}
          maxLength={maxLength}
          minLength={minLength}
          pattern={pattern}
          className={cn(
            `
            ${props.disabled
              ? "bg-gray-200 opacity-60 cursor-not-allowed"
              : "bg-white"
            }
            text-black text-sm md:text-base w-full
            ${icon ? "pl-10" : "pl-3"}
            pr-3 py-2 border rounded-md
            focus:outline-none focus:ring-[1.5px] focus:ring-green-12
            transition-all duration-300
            ${type !== "email" ? "uppercase" : ""}
            `,
          )}

          {...props}
        />
        {toolTips && (
          <div className="absolute right-2">
            {<Information label={toolTips} />}
          </div>
        )}
      </div>
      {error && <p className="text-sm font-medium text-red-500">{error}</p>}
    </div>
  );
};

export default Input;
